import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  Divider,
  MenuItem,
  Chip,
  Slide,
} from '../../../ui/core';
import { CloseIcon, ContentCopyIcon } from '../../../ui/icons';
import { useForm, useFormState } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useCopyToClipboard, useToast } from '../../../hooks';
import {
  updateVehicle,
  deleteVehicle,
  fetchVehicles,
  unassignVehicleFromTrip,
} from '../../../redux/Fleet/actions';
import { FleetVehicleStatusOptions, getVehicleDisplayStatus } from '../../../models/Fleet';
import ConfirmModal from '../../shared/ConfirmModal';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" timeout={900} ref={ref} {...props} />;
});

const style = {
  dataList: {
    display: 'grid',
    gridTemplateColumns: '124px 1fr',
    rowGap: '8px',
    margin: 0,
    '> dt': {
      gridColumn: 1,
      height: '48px',
      display: 'flex',
      alignItems: 'center',
    },
    '> dd': {
      gridColumn: 2,
      minHeight: '48px',
      display: 'flex',
      alignItems: 'center',
      margin: 0,
    },
  },
  fields: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    '.MuiTextField-root': { width: '250px' },
  },
};

const displayStatusColors = {
  AVAILABLE: {
    bg: '#DCFCE7',
    text: '#166534',
    bgDark: 'rgba(74, 222, 128, 0.18)',
    textDark: '#86EFAC',
    label: 'Available',
  },
  IN_USE: {
    bg: '#DBEAFE',
    text: '#1E40AF',
    bgDark: 'rgba(96, 165, 250, 0.18)',
    textDark: '#93C5FD',
    label: 'In Use',
  },
  UNAVAILABLE: {
    bg: '#FEE2E2',
    text: '#991B1B',
    bgDark: 'rgba(248, 113, 113, 0.18)',
    textDark: '#FCA5A5',
    label: 'Unavailable',
  },
};

// ─── Vehicle Details Slide-out ──────────────────────────────────────────────

const VehicleDetailsDialog = ({ vehicle, onClose }) => {
  const dispatch = useDispatch();
  const c = useCopyToClipboard();
  const t = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [unassignTripId, setUnassignTripId] = useState(null);

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      displayName: vehicle.displayName || '',
      vin: vehicle.vin || '',
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year || '',
      notes: vehicle.notes || '',
      status: vehicle.status || 'AVAILABLE',
    },
  });
  const { isDirty, isSubmitting } = useFormState({ control: form.control });

  const displayStatus = getVehicleDisplayStatus(vehicle);
  const statusColor = displayStatusColors[displayStatus] || displayStatusColors.AVAILABLE;
  const assignments = Array.isArray(vehicle.assignments) ? vehicle.assignments : [];

  const copyIdToClipboard = () => {
    c.copy(vehicle.id);
    t.success('Vehicle ID copied to clipboard');
  };

  const onSubmit = async (data) => {
    const { success } = await updateVehicle(vehicle.id, data)(dispatch);
    if (success) {
      t.success('Vehicle updated');
      fetchVehicles()(dispatch);
      onClose();
    } else {
      t.error('Failed to update vehicle');
    }
  };

  const onDelete = async () => {
    setConfirmDelete(false);
    const { success } = await deleteVehicle(vehicle.id)(dispatch);
    if (success) {
      t.success('Vehicle deleted');
      fetchVehicles()(dispatch);
      onClose();
    } else {
      t.error('Cannot delete vehicle — it may have an active trip assignment');
    }
  };

  const onUnassign = async () => {
    const tripId = unassignTripId;
    setUnassignTripId(null);
    const { success } = await unassignVehicleFromTrip(tripId, vehicle.id)(dispatch);
    if (success) {
      t.success('Vehicle unassigned from trip');
      fetchVehicles()(dispatch);
    } else {
      t.error('Failed to unassign vehicle');
    }
  };

  return (
    <>
      <Dialog
        sx={{ left: 'auto', width: '50%', minWidth: '600px' }}
        fullScreen
        open={!!vehicle}
        TransitionComponent={Transition}
        onClose={onClose}
      >
        {/* ─── Header ──────────────────────────────────────── */}
        <Stack
          position="relative"
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          spacing="16px"
          py="6px"
          px="48px"
          minHeight="56px"
        >
          <IconButton
            sx={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: '8px' }}
            onClick={onClose}
          >
            <CloseIcon sx={{ color: 'text.secondary' }} />
          </IconButton>
          <Box display="flex" alignItems="center">
            <Typography ml="5px" mr="10px">
              {vehicle.id}
            </Typography>
            <IconButton size="small" onClick={copyIdToClipboard}>
              <ContentCopyIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          </Box>
          <Chip
            label={statusColor.label}
            size="small"
            sx={(theme) => ({
              backgroundColor: theme.palette.mode === 'dark' ? statusColor.bgDark : statusColor.bg,
              color: theme.palette.mode === 'dark' ? statusColor.textDark : statusColor.text,
              fontWeight: 600,
            })}
          />
        </Stack>
        <Divider />

        <DialogContent sx={{ padding: '32px 40px' }}>
          {/* ─── Detail View ──────────────────────────────── */}
          {!isEditing && (
            <Stack spacing="32px">
              <Typography fontSize="28px" fontWeight={700}>{vehicle.displayName}</Typography>

              <Box component="dl" sx={style.dataList}>
                <dt><Typography color="text.secondary">Make</Typography></dt>
                <dd><Typography>{vehicle.make || '—'}</Typography></dd>
                <dt><Typography color="text.secondary">Model</Typography></dt>
                <dd><Typography>{vehicle.model || '—'}</Typography></dd>
                <dt><Typography color="text.secondary">Year</Typography></dt>
                <dd><Typography>{vehicle.year || '—'}</Typography></dd>
                <dt><Typography color="text.secondary">VIN</Typography></dt>
                <dd><Typography>{vehicle.vin || '—'}</Typography></dd>
                <dt><Typography color="text.secondary">Status</Typography></dt>
                <dd><Typography>{statusColor.label}</Typography></dd>
                {vehicle.notes && (
                  <>
                    <dt><Typography color="text.secondary">Notes</Typography></dt>
                    <dd><Typography>{vehicle.notes}</Typography></dd>
                  </>
                )}
              </Box>

              <Divider />

              {/* ─── Trip Assignments ─────────────────────────── */}
              <Box>
                <Typography variant="h2" mb="16px">
                  Trip Assignments {assignments.length > 0 && `(${assignments.length})`}
                </Typography>
                {assignments.length > 0 ? (
                  <Stack spacing={1.5}>
                    {assignments.map((a) => (
                      <Stack
                        key={a.id}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: '6px',
                          padding: '12px 16px',
                        }}
                      >
                        <Box
                          component="a"
                          href={`/trips/daily?tripId=${a.tripMongoId}`}
                          sx={{ color: 'accent.main', fontWeight: 500, textDecoration: 'none' }}
                        >
                          {a.tripMongoId}
                        </Box>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => setUnassignTripId(a.tripMongoId)}
                        >
                          Unassign
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography color="text.secondary">No trip assignments</Typography>
                )}
              </Box>

              <Divider />

              {/* ─── Actions ────────────────────────────────── */}
              <Stack direction="row" spacing={2}>
                <Button variant="contained" disableElevation onClick={() => setIsEditing(true)}>
                  Edit Vehicle
                </Button>
                <Button variant="outlined" color="error" onClick={() => setConfirmDelete(true)}>
                  Delete Vehicle
                </Button>
              </Stack>
            </Stack>
          )}

          {/* ─── Edit View ──────────────────────────────── */}
          {isEditing && (
            <Stack spacing="32px">
              <Typography fontSize="28px" fontWeight={700}>Edit Vehicle</Typography>
              <Box component="form" id="edit-vehicle-form" onSubmit={form.handleSubmit(onSubmit)}>
                <Box sx={style.fields}>
                  <TextField label="Display Name" control={form.control} name="displayName" rules={{ required: 'Required' }} />
                  <TextField label="Make" control={form.control} name="make" />
                  <TextField label="Model" control={form.control} name="model" />
                  <TextField label="Year" control={form.control} name="year" />
                  <TextField label="VIN" control={form.control} name="vin" />
                  <TextField label="Status" control={form.control} name="status" select>
                    {FleetVehicleStatusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField label="Notes" control={form.control} name="notes" sx={{ width: '100% !important' }} multiline rows={2} />
                </Box>
              </Box>
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  form="edit-vehicle-form"
                  disabled={!isDirty}
                  loading={isSubmitting}
                  variant="contained"
                  disableElevation
                >
                  Save Changes
                </Button>
              </Stack>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Confirm Delete Modal ────────────────────────────── */}
      <ConfirmModal
        open={confirmDelete}
        title="Delete Vehicle"
        message={`Are you sure you want to delete "${vehicle.displayName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      {/* ─── Confirm Unassign Modal ──────────────────────────── */}
      <ConfirmModal
        open={!!unassignTripId}
        title="Unassign Vehicle"
        message={`Unassign "${vehicle.displayName}" from trip ${unassignTripId}? The trip will no longer be a fleet trip.`}
        confirmLabel="Unassign"
        confirmColor="error"
        onConfirm={onUnassign}
        onCancel={() => setUnassignTripId(null)}
      />
    </>
  );
};

export default VehicleDetailsDialog;
